import { useCallback, useEffect, useRef, useState } from 'react';
import { TierPeriod } from 'features/premium/entities';
import useStripePayment from 'features/premium/hooks/useStripePayment';
import { selectShouldShowStripe } from 'features/premium/selectors';
import { getDowngradeOffer, } from 'features/promotions/actions';
import { muteAll, useFetchOld } from 'hooks/useFetch';
import { useAppSelector, useAppStore } from 'store/hooks';
import { getStripeProduct, getPrices, } from 'features/promotions/entities';
export const getStripeDowngradeDiscountData = (result, stripePriceMap) => result.stripeOffers
    .map((stripeOffer) => {
    const matchedSubscription = getStripeProduct(stripePriceMap, stripeOffer.productId);
    if (!matchedSubscription)
        return undefined;
    const discount = {
        type: 'stripe',
        discount: {
            couponId: stripeOffer.couponId,
            price: stripeOffer.discountPrice.toString(),
            numberOfPeriods: stripeOffer.periods,
            currency: result.currency,
        },
    };
    const monthlySubscription = matchedSubscription.period === TierPeriod.yearly
        ? stripePriceMap[TierPeriod.monthly][matchedSubscription.tier]
        : null;
    const formattedPrices = getPrices(matchedSubscription, monthlySubscription || null, discount, matchedSubscription.period, false, !!stripeOffer.discountAmount // true If fixed amount off, else false if percentage
    );
    return {
        type: 'stripe',
        subscription: matchedSubscription,
        discount,
        formattedPrices: {
            ...formattedPrices,
            percentOff: stripeOffer.discount || formattedPrices?.percentOff,
            amountOff: stripeOffer.discountAmount,
        },
    };
})
    .filter((data) => data != null);
const useDowngradeOffer = (loadOnMount = true) => {
    const [, , , fetchOffer] = useFetchOld(muteAll);
    const store = useAppStore();
    const { stripePriceMap } = useStripePayment();
    const shouldShowStripe = useAppSelector(selectShouldShowStripe);
    const discountData = useRef(undefined);
    const fairPriceOffersRef = useRef(undefined);
    const loadDiscountData = useCallback(async () => {
        // Only proceed if shouldShowStripe is true
        if (!shouldShowStripe) {
            return;
        }
        const state = store.getState();
        const { productId } = state.user.user.emmaProStatus;
        if (!productId)
            return;
        const result = await fetchOffer(getDowngradeOffer());
        if (result?.stripeOffers && stripePriceMap) {
            discountData.current = getStripeDowngradeDiscountData(result, stripePriceMap);
        }
        if (result?.fairPriceOffers) {
            fairPriceOffersRef.current = {
                currency: result.currency,
                fairPriceOffers: result.fairPriceOffers,
            };
        }
    }, [fetchOffer, stripePriceMap, shouldShowStripe]);
    useEffect(() => {
        if (loadOnMount && shouldShowStripe) {
            loadDiscountData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowStripe]);
    const [isRetrying, setIsRetrying] = useState(false);
    const retry = useCallback(async () => {
        if (!shouldShowStripe) {
            return;
        }
        // Return after a max of 3 seconds so the buttons don't get stuck
        setIsRetrying(true);
        const timeout = new Promise((resolve) => {
            setTimeout(resolve, 3000);
        });
        await Promise.race([timeout, loadDiscountData()]);
        setIsRetrying(false);
    }, [loadDiscountData, shouldShowStripe]);
    return {
        retry,
        isFetching: isRetrying,
        downgradeDiscountDataRef: discountData,
        fairPriceOffersRef,
        shouldShowStripe,
    };
};
export default useDowngradeOffer;
